import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { IconButton } from "@mui/material";
import {
    PersonRounded,
    AnnouncementRounded,
    PhoneRounded,
    QuestionAnswerRounded,
    GavelRounded,
    ExitToAppRounded,
    CloseRounded,
    DoneRounded,
    Settings
} from "@mui/icons-material";

import goLogo from "commons/src/css/images/logos/Go.png";

import { UserDetails } from "./UserDetails";
import { LiveSupport } from "../helpers/ContactHelpers";
import { routes } from "../helpers/RouteHelpers";

export function SidebarContent({ hideBar, logout, isBranded }) {
    const apolloClient = useApolloClient();
    const logoutClick = () => {
        hideBar();
        logout({
            message: "You have been successfully logged out",
            severity: "INFO",
            callback: () => apolloClient.resetStore()
        });
    };

    const openLiveSupport = () => new LiveSupport().open();

    return (
        <Content data-testid="sidebar-content">
            <StyledButton
                color="secondary"
                onClick={hideBar}
                data-testid="hide-sidebar-button"
                aria-label="close sidebar"
            >
                <CloseRounded fontSize="large" />
            </StyledButton>
            <UserDetails isSideBar />
            <NavUl>
                <Li>
                    <NavLink
                        data-testid="personal-details-button"
                        to={routes.client.personal()}
                        onClick={hideBar}
                    >
                        <CircledIcon aria-hidden="true">
                            <PersonRounded fontSize="small" />
                        </CircledIcon>
                        <LinkText>Personal details</LinkText>
                    </NavLink>
                </Li>
                <Li>
                    <NavLink
                        data-testid="authorisations-button"
                        to={routes.client.authorisations()}
                        onClick={hideBar}
                    >
                        <CircledIcon aria-hidden="true">
                            <DoneRounded fontSize="small" />
                        </CircledIcon>
                        <LinkText>Authorisations</LinkText>
                    </NavLink>
                </Li>
                <Li>
                    <NavLink data-testid="news-button" to={routes.client.news()} onClick={hideBar}>
                        <CircledIcon aria-hidden="true">
                            <AnnouncementRounded fontSize="small" />
                        </CircledIcon>
                        <LinkText>News</LinkText>
                    </NavLink>
                </Li>
                <Li>
                    <NavLink
                        data-testid="contacts-button"
                        to={routes.client.contacts()}
                        onClick={hideBar}
                    >
                        <CircledIcon aria-hidden="true">
                            <PhoneRounded fontSize="small" />
                        </CircledIcon>
                        <LinkText>Contacts</LinkText>
                    </NavLink>
                </Li>
                <Li>
                    <SupportLink data-testid="live-support-button" onClick={openLiveSupport}>
                        <CircledIcon aria-hidden="true">
                            <QuestionAnswerRounded fontSize="small" />
                        </CircledIcon>
                        <LinkText>Live support</LinkText>
                    </SupportLink>
                </Li>
                <Li>
                    <NavLink
                        data-testid="legal-info-button"
                        to={routes.client.legal()}
                        onClick={hideBar}
                    >
                        <CircledIcon aria-hidden="true">
                            <GavelRounded fontSize="small" />
                        </CircledIcon>
                        <LinkText>Legal info</LinkText>
                    </NavLink>
                </Li>
                <Li>
                    <NavLink
                        to={routes.client.settings()}
                        onClick={hideBar}
                        data-testid="settings-button"
                    >
                        <CircledIcon aria-hidden="true">
                            <Settings fontSize="small" />
                        </CircledIcon>
                        <LinkText>Settings</LinkText>
                    </NavLink>
                </Li>
                <Li>
                    <NavLink
                        to={routes.public.login()}
                        onClick={logoutClick}
                        data-testid="logout-button"
                    >
                        <CircledIcon aria-hidden="true">
                            <ExitToAppRounded fontSize="small" />
                        </CircledIcon>
                        <LinkText>Log out</LinkText>
                    </NavLink>
                </Li>
            </NavUl>
            {isBranded && (
                <LogoDiv data-testid="logo-image">
                    <Logo src={goLogo} />
                </LogoDiv>
            )}
        </Content>
    );
}

const StyledButton = styled(IconButton)`
    && {
        align-self: flex-end;
    }
`;

export const CircledIcon = styled.div.attrs({ "data-testid": "circled-icon" })`
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1rem;
    color: ${props => props.theme.tertiary};
    background: ${props => props.theme.secondary};
    border-radius: 50%;
    width: 2.1rem;
    height: 2.1rem;
    text-align: center;
    overflow: hidden;
    margin: 0 0.5em 0 0;
    box-sizing: content-box;
`;

export const LinkText = styled.span`
    color: ${props => props.theme.secondary};
    font-style: normal;
    font-family: vag-rounded-light, Helvetica, Arial, sans-serif;
    font-weight: 200;
    margin-bottom: -2px;
`;

export const NavLink = styled(Link)`
    color: inherit;
    margin-top: 1em;
    font-size: 1.14em;
    display: flex;
    text-decoration: none;
    align-items: center;
`;

export const SupportLink = styled.button`
    padding-left: 0;
    color: inherit;
    margin-top: 1em;
    font-size: 1.14em;
    display: flex;
    text-decoration: none;
    align-items: center;
    cursor: pointer;
    background: transparent;
    border: none;
    outline: none;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #fff;
    padding: 2em 1.5em 2em 2em;
    overflow: auto;
    width: 320px;

    @media (max-width: 760px) {
        padding: 0.8em 1em 2em 2em;
        font-size: 90%;
        width: 220px;
    }
`;

export const LogoDiv = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    height: 100%;
    width: 100%;
    min-height: 90px;
`;

const Li = styled.li`
    display: block;
    padding: 0;
    text-align: left;
    margin: 0;
`;

const NavUl = styled.ul`
    margin-top: 2em;
    padding: 0;
    width: 100%;
    list-style: none;
`;

export const Logo = styled.img`
    max-width: 90px;
    max-height: 90px;
`;

SidebarContent.propTypes = {
    hideBar: PropTypes.func,
    isBranded: PropTypes.bool,
    logout: PropTypes.func
};
